import { supabase } from "@/integrations/supabase/client";

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export interface ChatThread {
  id: string;
  title: string;
  created_at: string;
  preview: string;
}

export interface AIRequestConfig {
  enable_bellwether?: boolean;
  enable_macro?: boolean;
}

// Calendar Alert Types
export interface CalendarAlert {
  id: number;
  timestamp: string;
  analysis_period_start: string;
  volatile_window_start: string;
  volatile_window_end: string;
  strykr_score: number;
  window_volatility_rating: number;
  window_volatility_intensity: string;
  short_summary: string;
  full_analysis: string;
  events_analyzed: Array<{
    name: string;
    impact: string;
    country: string;
    currency: string;
  }>;
  sent?: boolean;
}

// Market Screener Types
export interface StockRecommendation {
  symbol: string;
  name: string;
  entry_price: number;
  confidence_score: number;
  timeframe: string;
  change_percent?: number;
}

export interface MarketScreenerData {
  id: number;
  timestamp: string;
  analysis_date: string;
  top_stocks_long: StockRecommendation[];
  top_stocks_short: StockRecommendation[];
  top_cryptos_long: StockRecommendation[];
  top_cryptos_short: StockRecommendation[];
}

// Market Alert Types
export interface MarketAlertCondition {
  extended_summary: string;
  market_conditions: string;
  key_movements: string[];
  correlations: string[];
  risk_factors: string[];
  opportunities: string[];
}

export interface MarketAlert {
  id: number;
  timestamp: string;
  risk_level: 'LOW' | 'MEDIUM' | 'HIGH';
  short_summary: string;
  full_analysis: MarketAlertCondition;
  sent?: boolean;
}

// News Alert Types
export interface NewsAlert {
  id: number;
  timestamp: string;
  summary: string;
  sentiment: 'Bullish' | 'Neutral' | 'Bearish';
  sentiment_reasoning: string;
  news_articles: Array<{
    headline: string;
    source: string;
    date: string;
    snippet: string;
    url?: string; // Full article URL if available
  }>;
  alert_type?: 'news';
  sent?: boolean;
}

// Get API key helper function
async function getApiKey() {
  const { data: keyData, error: keyError } = await supabase.functions.invoke('get-strykr-key');
  if (keyError) throw new Error('Failed to get API key');
  if (!keyData?.key) throw new Error('API key not found');
  return keyData.key;
}

export async function sendChatMessage(
  message: string, 
  userId: string, 
  config: AIRequestConfig = {},
  conversationHistory?: ChatMessage[]
): Promise<string> {
  try {
    const apiKey = await getApiKey();

    // Build conversation context if we have history
    let contextualQuery = message;
    if (conversationHistory && conversationHistory.length > 1) {
      // Filter out the initial system message and empty messages
      const relevantMessages = conversationHistory
        .filter(msg => msg.content.trim() !== '' && !msg.content.includes('Welcome to Strykr AI'))
        .slice(-6); // Keep last 6 messages for context (3 Q&A pairs)
      
      if (relevantMessages.length > 0) {
        const context = relevantMessages
          .map(msg => `${msg.role === 'user' ? 'Human' : 'Assistant'}: ${msg.content}`)
          .join('\n\n');
        
        contextualQuery = `Previous conversation:\n${context}\n\nCurrent question: ${message}`;
        console.log("[sendChatMessage] Sending with context, history length:", relevantMessages.length);
      }
    }

    const requestBody = {
      query: contextualQuery,
      third_party_user_id: userId,
      stream: true, // Always use streaming
      enable_bellwether: Boolean(config.enable_bellwether),
      enable_macro: Boolean(config.enable_macro)
    };
    
    console.log("[sendChatMessage] Request body:", requestBody);
    
    try {
      // Get the auth token for the authenticated user
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No active session");
      
      console.log("[sendChatMessage] Setting up streaming with fetch");
      
      // Get the Supabase URL from environment variables
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
      
      // Verify URL is available
      if (!supabaseUrl) {
        throw new Error("Missing Supabase URL environment variable");
      }
      const functionUrl = `${supabaseUrl}/functions/v1/chat`;
      
      // Call the edge function using fetch with POST data
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      if (!response.body) {
        throw new Error("Response has no body");
      }
      
      // Get the reader from the response stream
      const reader = response.body.getReader();
      
      // Create a text decoder
      const decoder = new TextDecoder();
      
      // Simplified stream processing function
      const processStream = async () => {
        try {
          let buffer = '';
          let completeContent = '';
          let hasReceivedContent = false;
          
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
              // Finish processing any remaining content
              console.log("[sendChatMessage] Stream complete");
              
              // Only send completion events if we actually received content
              if (hasReceivedContent) {
                // Send the final event to indicate completion
                window.dispatchEvent(new CustomEvent('assistantResponse', {
                  detail: { final: true }
                }));
                
                // Send the complete content for storage
                window.dispatchEvent(new CustomEvent('assistantResponseComplete', {
                  detail: { completeContent }
                }));
              } else {
                // If no content received, it might be an error
                console.warn("[sendChatMessage] Stream ended without content");
                window.dispatchEvent(new CustomEvent('assistantResponse', {
                  detail: { 
                    content: "Sorry, I couldn't process your request. Please try again.", 
                    final: true 
                  }
                }));
              }
              
              break;
            }
            
            // Decode the chunk and add to buffer
            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;
            
            // Process complete SSE messages (ending with double newlines)
            const messages = buffer.split('\n\n');
            buffer = messages.pop() || '';
            
            for (const message of messages) {
              // Look for lines starting with "data: "
              const lines = message.split('\n');
              
              for (const line of lines) {
                if (line.startsWith('data:')) {
                  try {
                    // Extract JSON after "data: "
                    const jsonStr = line.substring(5).trim();
                    if (!jsonStr) continue;
                    
                    const data = JSON.parse(jsonStr);
                    
                    if (data && data.content) {
                      hasReceivedContent = true;
                      // Add to the complete content
                      completeContent += data.content;
                      
                      // Dispatch event with just the content
                      window.dispatchEvent(new CustomEvent('assistantResponse', {
                        detail: { content: data.content }
                      }));
                    }
                  } catch (e) {
                    console.error("[sendChatMessage] Error parsing SSE data:", e);
                    // Don't break the stream for parsing errors, continue processing
                  }
                }
              }
            }
          }
        } catch (error) {
          console.error("[sendChatMessage] Error processing stream:", error);
          window.dispatchEvent(new CustomEvent('assistantResponse', {
            detail: { 
              content: "An error occurred while processing your request. Please try again.", 
              final: true 
            }
          }));
        }
      };
      
      // Start processing the stream
      processStream();
      
      return ""; // Empty string as content will be streamed
    } catch (error) {
      console.error("[sendChatMessage] Error with chat streaming:", error);
      throw error;
    }
  } catch (error) {
    console.error("[sendChatMessage] Error:", error);
    const userMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
    throw new Error(userMessage);
  }
}

export async function storeChatHistory(
  userId: string, 
  question: string, 
  response: string,
  conversationId?: string
) {
  if (!userId) return { id: null, error: new Error('No user ID provided') };

  const id = crypto.randomUUID();
  
  // If no conversationId provided, create a new one for this conversation
  const finalConversationId = conversationId || crypto.randomUUID();

  try {
    const { error: historyError } = await supabase
      .from('chat_history')
      .insert({
        id,
        user_id: userId,
        question: question,
        response: response,
        conversation_id: finalConversationId
      });

    if (historyError) {
      console.error('[storeChatHistory] Error:', historyError);
      return { id: null, error: historyError };
    }

    // Return the conversation ID so it can be used for subsequent messages
    return { id: finalConversationId, error: null };
  } catch (error) {
    console.error('[storeChatHistory] Unexpected error:', error);
    return { id: null, error: error as Error };
  }
}

// Simple thread fetching - gets conversation starters only
export async function fetchChatThreads(userId: string) {
  try {
    const { data, error } = await supabase
      .from('chat_history')
      .select('id, question, created_at, conversation_id')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
      
    if (error) {
      console.error("[fetchChatThreads] Error:", error);
      return { threads: [], error };
    }
    
    // Group by conversation_id and take the first message of each thread
    const threadMap = new Map();
    data?.forEach(item => {
      const threadId = item.conversation_id || item.id;
      if (!threadMap.has(threadId)) {
        threadMap.set(threadId, {
          id: threadId,
          title: item.question.substring(0, 60) + (item.question.length > 60 ? '...' : ''),
          created_at: item.created_at,
          preview: item.question
        });
      }
    });
    
    return { threads: Array.from(threadMap.values()), error: null };
  } catch (error) {
    console.error("[fetchChatThreads] Error:", error);
    return { threads: [], error };
  }
}

// Fetch all messages in a thread
export async function fetchThreadMessages(threadId: string, userId: string) {
  try {
    const { data, error } = await supabase
      .from('chat_history')
      .select('*')
      .eq('user_id', userId)
      .eq('conversation_id', threadId)
      .order('created_at', { ascending: true });
      
    if (error) {
      console.error("[fetchThreadMessages] Error:", error);
      return { messages: [], error };
    }
    
    return { messages: data || [], error: null };
  } catch (error) {
    console.error("[fetchThreadMessages] Error:", error);
    return { messages: [], error };
  }
}

// Calendar Alerts API
export async function getCalendarAlerts(): Promise<CalendarAlert[]> {
  try {
    const apiKey = await getApiKey();
    
    console.log('[getCalendarAlerts] Fetching calendar alerts...');
    
    // Using the exact same approach as sendChatMessage
    const response = await fetch('https://strykr-api-staging.up.railway.app/api/alerts/calendar-alerts', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': apiKey,
      }
    });

    if (!response.ok) {
      let errorMessage = `Server error (${response.status})`;
      try {
        const errorBody = await response.text();
        console.error("[getCalendarAlerts] Server error response:", {
          status: response.status,
          statusText: response.statusText,
          body: errorBody
        });
        errorMessage = `Server error: ${response.status} - Please try again later`;
      } catch (parseError) {
        console.error("[getCalendarAlerts] Error parsing error response:", parseError);
        errorMessage = "An unexpected error occurred. Please try again.";
      }
      
      // For development purposes, return mock data if API fails
      if (process.env.NODE_ENV === 'development') {
        console.log('[getCalendarAlerts] Using mock data for development');
        return getMockCalendarAlerts();
      }
      
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('[getCalendarAlerts] Successfully fetched data:', data);
    return data;
  } catch (error) {
    console.error('[getCalendarAlerts] Error:', error);
    
    // For development purposes, return mock data on error
    if (process.env.NODE_ENV === 'development') {
      console.log('[getCalendarAlerts] Using mock data for development');
      return getMockCalendarAlerts();
    }
    
    throw error;
  }
}

export async function getCalendarAlert(alertId: number): Promise<CalendarAlert> {
  try {
    const apiKey = await getApiKey();
    
    console.log(`[getCalendarAlert] Fetching calendar alert ${alertId}...`);
    
    const response = await fetch(`https://strykr-api-staging.up.railway.app/api/alerts/calendar-alerts/${alertId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': apiKey,
      }
    });

    if (!response.ok) {
      let errorMessage = `Server error (${response.status})`;
      try {
        const errorBody = await response.text();
        console.error(`[getCalendarAlert] Server error response for alert ID ${alertId}:`, {
          status: response.status,
          statusText: response.statusText,
          body: errorBody
        });
        errorMessage = `Server error: ${response.status} - Please try again later`;
      } catch (parseError) {
        console.error(`[getCalendarAlert] Error parsing error response for alert ID ${alertId}:`, parseError);
        errorMessage = "An unexpected error occurred. Please try again.";
      }
      
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log(`[getCalendarAlert] Successfully fetched data for alert ID ${alertId}:`, data);
    return data;
  } catch (error) {
    console.error(`[getCalendarAlert] Error for alert ID ${alertId}:`, error);
    throw error;
  }
}

export async function markCalendarAlertAsSent(alertId: number): Promise<{ message: string }> {
  try {
    const apiKey = await getApiKey();
    
    console.log(`[markCalendarAlertAsSent] Marking calendar alert ${alertId} as sent...`);
    
    const response = await fetch(`https://strykr-api-staging.up.railway.app/api/alerts/calendar-alerts/${alertId}/sent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': apiKey,
      }
    });

    if (!response.ok) {
      let errorMessage = `Server error (${response.status})`;
      try {
        const errorBody = await response.text();
        console.error(`[markCalendarAlertAsSent] Server error response for alert ID ${alertId}:`, {
          status: response.status,
          statusText: response.statusText,
          body: errorBody
        });
        errorMessage = `Server error: ${response.status} - Please try again later`;
      } catch (parseError) {
        console.error(`[markCalendarAlertAsSent] Error parsing error response for alert ID ${alertId}:`, parseError);
        errorMessage = "An unexpected error occurred. Please try again.";
      }
      
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log(`[markCalendarAlertAsSent] Successfully marked alert ID ${alertId} as sent:`, data);
    return data;
  } catch (error) {
    console.error(`[markCalendarAlertAsSent] Error for alert ID ${alertId}:`, error);
    throw error;
  }
}

// Mock data for calendar alerts
function getMockCalendarAlerts(): CalendarAlert[] {
  // Current time
  const now = new Date();
  
  // Start of volatile window (4 hours from now)
  const volatileStart = new Date(now.getTime() + 4 * 60 * 60 * 1000);
  
  // End of volatile window (6 hours from volatile start)
  const volatileEnd = new Date(volatileStart.getTime() + 6 * 60 * 60 * 1000);
  
  return [
    {
      id: 1,
      timestamp: now.toISOString(),
      analysis_period_start: now.toISOString(),
      strykr_score: 8.5,
      window_volatility_rating: 0.75,
      window_volatility_intensity: 'High',
      short_summary: 'New economic conditions are changing. The most volatile time of day is coming in the next 4 hours.',
      full_analysis: 'Markets are expected to experience increased volatility due to the upcoming Fed announcement. Trading volumes have already started to increase, and we expect significant price movements across major indices.\n\nOur analysis suggests that the USD may strengthen against other currencies, particularly EUR and JPY.',
      volatile_window_start: volatileStart.toISOString(),
      volatile_window_end: volatileEnd.toISOString(),
      events_analyzed: [
        { name: 'Fed Interest Rate Decision', impact: 'High', country: 'US', currency: 'USD' },
        { name: 'ECB Policy Statement', impact: 'Medium', country: 'EU', currency: 'EUR' }
      ],
      sent: false
    },
    {
      id: 2,
      timestamp: new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString(),
      analysis_period_start: new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString(),
      strykr_score: 5.2,
      window_volatility_rating: 0.45,
      window_volatility_intensity: 'Medium',
      short_summary: 'Moderately volatile conditions expected during European market open tomorrow.',
      full_analysis: 'European markets are likely to open with increased volatility due to overnight developments in Asian markets. Major European indices may experience fluctuations as traders react to economic data releases.',
      volatile_window_start: new Date(now.getTime() + 12 * 60 * 60 * 1000).toISOString(),
      volatile_window_end: new Date(now.getTime() + 16 * 60 * 60 * 1000).toISOString(),
      events_analyzed: [
        { name: 'German Manufacturing PMI', impact: 'Medium', country: 'DE', currency: 'EUR' },
        { name: 'UK Inflation Report', impact: 'High', country: 'UK', currency: 'GBP' }
      ],
      sent: true
    },
    {
      id: 3,
      timestamp: new Date(now.getTime() - 48 * 60 * 60 * 1000).toISOString(),
      analysis_period_start: new Date(now.getTime() - 48 * 60 * 60 * 1000).toISOString(),
      strykr_score: 3.1,
      window_volatility_rating: 0.25,
      window_volatility_intensity: 'Low',
      short_summary: 'Low volatility expected for commodity markets in the coming session.',
      full_analysis: 'Commodity markets are expected to trade within a narrow range today. Gold and oil may see limited price movement due to the absence of major economic catalysts.',
      volatile_window_start: new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString(),
      volatile_window_end: new Date(now.getTime() + 28 * 60 * 60 * 1000).toISOString(),
      events_analyzed: [
        { name: 'US Crude Oil Inventories', impact: 'Medium', country: 'US', currency: 'USD' },
        { name: 'OPEC Meeting', impact: 'High', country: 'SA', currency: 'USD' }
      ],
      sent: false
    }
  ];
}

// Market Screener API
export async function getStockRecommendations(): Promise<MarketScreenerData> {
  try {
    const apiKey = await getApiKey();
    
    console.log('[getStockRecommendations] Fetching stock recommendations...');
    
    // Using the exact same approach as sendChatMessage
    const response = await fetch('https://strykr-api-staging.up.railway.app/api/alerts/market-screener/stocks', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': apiKey,
      }
    });

    if (!response.ok) {
      let errorMessage = `Server error (${response.status})`;
      try {
        const errorBody = await response.text();
        console.error("[getStockRecommendations] Server error response:", {
          status: response.status,
          statusText: response.statusText,
          body: errorBody
        });
        errorMessage = `Server error: ${response.status} - Please try again later`;
      } catch (parseError) {
        console.error("[getStockRecommendations] Error parsing error response:", parseError);
        errorMessage = "An unexpected error occurred. Please try again.";
      }
      
      // For development purposes, return mock data if API fails
      if (process.env.NODE_ENV === 'development') {
        console.log('[getStockRecommendations] Using mock data for development');
        return getMockStockRecommendations();
      }
      
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('[getStockRecommendations] Successfully fetched data:', data);
    return data;
  } catch (error) {
    console.error('[getStockRecommendations] Error:', error);
    
    // For development purposes, return mock data on error
    if (process.env.NODE_ENV === 'development') {
      console.log('[getStockRecommendations] Using mock data for development');
      return getMockStockRecommendations();
    }
    
    throw error;
  }
}

export async function getCryptoRecommendations(): Promise<MarketScreenerData> {
  try {
    const apiKey = await getApiKey();
    
    console.log('[getCryptoRecommendations] Fetching crypto recommendations...');
    
    // Using the exact same approach as sendChatMessage
    const response = await fetch('https://strykr-api-staging.up.railway.app/api/alerts/market-screener/crypto', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': apiKey,
      }
    });

    if (!response.ok) {
      let errorMessage = `Server error (${response.status})`;
      try {
        const errorBody = await response.text();
        console.error("[getCryptoRecommendations] Server error response:", {
          status: response.status,
          statusText: response.statusText,
          body: errorBody
        });
        errorMessage = `Server error: ${response.status} - Please try again later`;
      } catch (parseError) {
        console.error("[getCryptoRecommendations] Error parsing error response:", parseError);
        errorMessage = "An unexpected error occurred. Please try again.";
      }
      
      // For development purposes, return mock data if API fails
      if (process.env.NODE_ENV === 'development') {
        console.log('[getCryptoRecommendations] Using mock data for development');
        return getMockCryptoRecommendations();
      }
      
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('[getCryptoRecommendations] Successfully fetched data:', data);
    return data;
  } catch (error) {
    console.error('[getCryptoRecommendations] Error:', error);
    
    // For development purposes, return mock data on error
    if (process.env.NODE_ENV === 'development') {
      console.log('[getCryptoRecommendations] Using mock data for development');
      return getMockCryptoRecommendations();
    }
    
    throw error;
  }
}

// Mock data for development purposes
function getMockStockRecommendations(): MarketScreenerData {
  return {
    id: 1,
    timestamp: new Date().toISOString(),
    analysis_date: new Date().toISOString().split('T')[0],
    top_stocks_long: [
      {
        symbol: 'AAPL',
        name: 'Apple Inc.',
        entry_price: 172.62,
        confidence_score: 0.87,
        timeframe: '1W',
        change_percent: 2.5
      },
      {
        symbol: 'MSFT',
        name: 'Microsoft Corporation',
        entry_price: 425.22,
        confidence_score: 0.85,
        timeframe: '2W',
        change_percent: 1.2
      },
      {
        symbol: 'NVDA',
        name: 'NVIDIA Corporation',
        entry_price: 881.87,
        confidence_score: 0.83,
        timeframe: '2W',
        change_percent: 1.5
      }
    ],
    top_stocks_short: [
      {
        symbol: 'GME',
        name: 'GameStop Corp.',
        entry_price: 18.45,
        confidence_score: 0.76,
        timeframe: '1W',
        change_percent: -2.5
      },
      {
        symbol: 'AMC',
        name: 'AMC Entertainment Holdings',
        entry_price: 4.75,
        confidence_score: 0.72,
        timeframe: '1W',
        change_percent: -1.5
      }
    ],
    top_cryptos_long: [],
    top_cryptos_short: []
  };
}

function getMockCryptoRecommendations(): MarketScreenerData {
  return {
    id: 2,
    timestamp: new Date().toISOString(),
    analysis_date: new Date().toISOString().split('T')[0],
    top_stocks_long: [],
    top_stocks_short: [],
    top_cryptos_long: [
      {
        symbol: 'BTC',
        name: 'Bitcoin',
        entry_price: 63420.25,
        confidence_score: 0.82,
        timeframe: '2W',
        change_percent: 1.5
      },
      {
        symbol: 'ETH',
        name: 'Ethereum',
        entry_price: 3018.75,
        confidence_score: 0.79,
        timeframe: '1W',
        change_percent: 0.5
      }
    ],
    top_cryptos_short: [
      {
        symbol: 'DOGE',
        name: 'Dogecoin',
        entry_price: 0.12,
        confidence_score: 0.68,
        timeframe: '1W',
        change_percent: -1.5
      }
    ]
  };
}

// Market Alerts API (these endpoints don't exist yet but will be added to backend)
export async function getMarketAlerts(): Promise<MarketAlert[]> {
  try {
    const apiKey = await getApiKey();
    
    console.log('[getMarketAlerts] Fetching market alerts...');
    
    // Using the exact same approach as sendChatMessage
    const response = await fetch('https://strykr-api-staging.up.railway.app/api/alerts/market-alerts', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': apiKey,
      }
    });

    if (!response.ok) {
      let errorMessage = `Server error (${response.status})`;
      try {
        const errorBody = await response.text();
        console.error("[getMarketAlerts] Server error response:", {
          status: response.status,
          statusText: response.statusText,
          body: errorBody
        });
        errorMessage = `Server error: ${response.status} - Please try again later`;
      } catch (parseError) {
        console.error("[getMarketAlerts] Error parsing error response:", parseError);
        errorMessage = "An unexpected error occurred. Please try again.";
      }
      
      // For development purposes, return mock data if API fails
      if (process.env.NODE_ENV === 'development') {
        console.log('[getMarketAlerts] Using mock data for development');
        return getMockMarketAlerts();
      }
      
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('[getMarketAlerts] Successfully fetched data:', data);
    return data;
  } catch (error) {
    console.error('[getMarketAlerts] Error:', error);
    
    // For development purposes, return mock data on error
    if (process.env.NODE_ENV === 'development') {
      console.log('[getMarketAlerts] Using mock data for development');
      return getMockMarketAlerts();
    }
    
    throw error;
  }
}

export async function getMarketAlert(alertId: number): Promise<MarketAlert> {
  try {
    const apiKey = await getApiKey();
    
    console.log(`[getMarketAlert] Fetching market alert ${alertId}...`);
    
    const response = await fetch(`https://strykr-api-staging.up.railway.app/api/alerts/market-alerts/${alertId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': apiKey,
      }
    });

    if (!response.ok) {
      let errorMessage = `Server error (${response.status})`;
      try {
        const errorBody = await response.text();
        console.error(`[getMarketAlert] Server error response for alert ID ${alertId}:`, {
          status: response.status,
          statusText: response.statusText,
          body: errorBody
        });
        errorMessage = `Server error: ${response.status} - Please try again later`;
      } catch (parseError) {
        console.error(`[getMarketAlert] Error parsing error response for alert ID ${alertId}:`, parseError);
        errorMessage = "An unexpected error occurred. Please try again.";
      }
      
      // For development purposes, return mock data if API fails
      if (process.env.NODE_ENV === 'development') {
        const mockAlerts = getMockMarketAlerts();
        const alert = mockAlerts.find(a => a.id === alertId);
        if (alert) {
          console.log(`[getMarketAlert] Using mock data for alert ID ${alertId}`);
          return alert;
        }
      }
      
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log(`[getMarketAlert] Successfully fetched data for alert ID ${alertId}:`, data);
    return data;
  } catch (error) {
    console.error(`[getMarketAlert] Error for alert ID ${alertId}:`, error);
    
    // For development purposes, return mock data on error
    if (process.env.NODE_ENV === 'development') {
      const mockAlerts = getMockMarketAlerts();
      const alert = mockAlerts.find(a => a.id === alertId);
      if (alert) {
        console.log(`[getMarketAlert] Using mock data for alert ID ${alertId}`);
        return alert;
      }
    }
    
    throw error;
  }
}

export async function markMarketAlertAsSent(alertId: number): Promise<{ message: string }> {
  try {
    const apiKey = await getApiKey();
    
    console.log(`[markMarketAlertAsSent] Marking market alert ${alertId} as sent...`);
    
    const response = await fetch(`https://strykr-api-staging.up.railway.app/api/alerts/market-alerts/${alertId}/sent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': apiKey,
      }
    });

    if (!response.ok) {
      let errorMessage = `Server error (${response.status})`;
      try {
        const errorBody = await response.text();
        console.error(`[markMarketAlertAsSent] Server error response for alert ID ${alertId}:`, {
          status: response.status,
          statusText: response.statusText,
          body: errorBody
        });
        errorMessage = `Server error: ${response.status} - Please try again later`;
      } catch (parseError) {
        console.error(`[markMarketAlertAsSent] Error parsing error response for alert ID ${alertId}:`, parseError);
        errorMessage = "An unexpected error occurred. Please try again.";
      }
      
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log(`[markMarketAlertAsSent] Successfully marked alert ID ${alertId} as sent:`, data);
    return data;
  } catch (error) {
    console.error(`[markMarketAlertAsSent] Error for alert ID ${alertId}:`, error);
    throw error;
  }
}

// Mock data for market alerts
function getMockMarketAlerts(): MarketAlert[] {
  return [
    {
      id: 1,
      timestamp: new Date().toISOString(),
      risk_level: 'HIGH',
      short_summary: '🔴 HIGH RISK: SPY and QQQ showing bearish divergence with increased volatility',
      full_analysis: {
        extended_summary: 'Major indices showing technical weakness with RSI divergence and declining volume.',
        market_conditions: 'Bearish sentiment with increased volatility',
        key_movements: ['SPY down 2.5%', 'VIX up 15%'],
        correlations: ['Inverse correlation between equities and treasuries strengthening'],
        risk_factors: ['Technical breakdown of support levels', 'Reduced market liquidity'],
        opportunities: ['Defensive sectors showing relative strength', 'Gold performing as safe haven']
      },
      sent: false
    },
    {
      id: 2,
      timestamp: new Date(Date.now() - 86400000).toISOString(), // Yesterday
      risk_level: 'MEDIUM',
      short_summary: '🟠 MEDIUM RISK: Market is very bullish today. SPX is going up.',
      full_analysis: {
        extended_summary: 'Markets showing signs of indecision with mixed technical signals.',
        market_conditions: 'Neutral sentiment with pockets of strength',
        key_movements: ['Tech sector up 1.2%', 'Energy sector down 0.8%'],
        correlations: ['Sector rotation evident with money flowing from energy to tech'],
        risk_factors: ['Resistance levels approaching for major indices', 'Economic data mixed'],
        opportunities: ['Tech stocks showing momentum', 'Short-term trading opportunities in selected names']
      },
      sent: true
    },
    {
      id: 3,
      timestamp: new Date(Date.now() - 86400000 * 2).toISOString(), // Two days ago
      risk_level: 'LOW',
      short_summary: '🟢 LOW RISK: Stable market conditions with low volatility expected',
      full_analysis: {
        extended_summary: 'Market conditions appear stable with low volatility metrics across major indices.',
        market_conditions: 'Bullish sentiment with low volatility',
        key_movements: ['Broad market up 0.5%', 'VIX down 5%'],
        correlations: ['Positive correlation between risk assets strengthening'],
        risk_factors: ['Complacency rising', 'Valuations stretched in some sectors'],
        opportunities: ['Long-term positioning favored', 'Low volatility strategies performing well']
      },
      sent: false
    }
  ];
}

